import * as Sentry from '@sentry/react'

enum HTTP_METHOD {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  PATCH = 'PATCH',
  DELETE = 'DELETE',
}

type RequestStructure = {
  method: HTTP_METHOD
  path: string
  headers?: Record<string, string>
  body?: string | object
  signal?: AbortSignal
}

class VariantLensAPIClient {
  // Local dev: VITE_API_BASE_URL=/api so Vite proxies to variant-lens-api (default https://localhost:8002)
  private static baseUrl: string = import.meta.env['VITE_API_BASE_URL'] ?? (import.meta.env['DEV'] ? '/api' : '')
  private getAccessToken?: () => Promise<string>
  private static instance: VariantLensAPIClient | null = null

  private constructor(getAccessToken?: () => Promise<string>) {
    this.getAccessToken = getAccessToken
  }

  static getInstance(getAccessToken?: () => Promise<string>): VariantLensAPIClient {
    if (!VariantLensAPIClient.instance) {
      VariantLensAPIClient.instance = new VariantLensAPIClient(getAccessToken)
    } else if (getAccessToken) {
      VariantLensAPIClient.instance.getAccessToken = getAccessToken
    }
    return VariantLensAPIClient.instance
  }

  static resetInstance(): void {
    VariantLensAPIClient.instance = null
  }

  public setTokenGetter(getAccessToken: () => Promise<string>) {
    this.getAccessToken = getAccessToken
  }

  private async request(request: RequestStructure, withAuth: boolean = true): Promise<Response> {
    return Sentry.startSpan({ op: 'http.client', name: `${request.method} ${request.path}` }, async (span) => {
      span.setAttribute('http.method', request.method)
      span.setAttribute('http.url', `${VariantLensAPIClient.baseUrl}${request.path}`)
      span.setAttribute('with_auth', withAuth)

      const outgoingHeaders: Record<string, string> = request.headers || {}

      if (withAuth) {
        if (this.getAccessToken) {
          try {
            const accessToken = await this.getAccessToken()
            outgoingHeaders['Authorization'] = `Bearer ${accessToken}`
            span.setAttribute('auth_method', 'jwt')
          } catch (error) {
            console.error('[VariantLensAPIClient] Failed to get access token:', error)
            span.setAttribute('auth_error', true)
          }
        } else {
          console.warn('[VariantLensAPIClient] Auth requested but no token getter available')
        }
      }

      let processedBody: string | undefined
      if (request.body !== undefined && request.body !== null) {
        processedBody = typeof request.body === 'string' ? request.body : JSON.stringify(request.body)
        if (typeof request.body !== 'string') {
          outgoingHeaders['Content-Type'] = 'application/json'
        }
      }

      try {
        const response = await fetch(`${VariantLensAPIClient.baseUrl}${request.path}`, {
          method: request.method,
          headers: outgoingHeaders,
          body: processedBody,
          signal: request.signal,
        })
        span.setAttribute('http.status_code', response.status)
        return response
      } catch (error) {
        span.setAttribute('error', true)
        if (error instanceof Error) {
          span.setAttribute('error.message', error.message)
        }
        Sentry.captureException(error, {
          tags: {
            operation: 'http.client',
            method: request.method,
            path: request.path,
          },
        })
        throw error
      }
    })
  }

  public async get(
    path: string,
    headers?: Record<string, string>,
    withAuth: boolean = true,
    signal?: AbortSignal
  ): Promise<Response> {
    return this.request({ method: HTTP_METHOD.GET, path, headers, signal }, withAuth)
  }

  public async post(
    path: string,
    body?: string | object,
    headers?: Record<string, string>,
    withAuth: boolean = true,
    signal?: AbortSignal
  ): Promise<Response> {
    return this.request({ method: HTTP_METHOD.POST, path, body, headers, signal }, withAuth)
  }

  public async put(
    path: string,
    body?: string | object,
    headers?: Record<string, string>,
    withAuth: boolean = true,
    signal?: AbortSignal
  ): Promise<Response> {
    return this.request({ method: HTTP_METHOD.PUT, path, body, headers, signal }, withAuth)
  }

  public async patch(
    path: string,
    body?: string | object,
    headers?: Record<string, string>,
    withAuth: boolean = true,
    signal?: AbortSignal
  ): Promise<Response> {
    return this.request({ method: HTTP_METHOD.PATCH, path, body, headers, signal }, withAuth)
  }

  public async delete(
    path: string,
    headers?: Record<string, string>,
    withAuth: boolean = true,
    signal?: AbortSignal
  ): Promise<Response> {
    return this.request({ method: HTTP_METHOD.DELETE, path, headers, signal }, withAuth)
  }
}

export default VariantLensAPIClient
